import { collection, getDocs, query, setDoc, updateDoc, doc, deleteDoc, orderBy, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase_config';
import { EVENT_COLLECTION, EVENT_SIGNUP_COLLECTION, Event, EventSignUp } from '../../type';

class EventService {
  async getAllEvents(): Promise<Event[]> {
    const q = query(collection(db, EVENT_COLLECTION), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ 
      ...doc.data(),
      eventId: doc.id 
    } as Event));
  }

  async getEventsQuery(startDate?: string, endDate?: string): Promise<Event[]> {
    let q = query(collection(db, EVENT_COLLECTION), orderBy('createdAt', 'desc'));

    if (startDate) {
      const startTimestamp = Timestamp.fromDate(new Date(startDate));
      q = query(q, where('createdAt', '>=', startTimestamp));
    }

    if (endDate) {
      const endTimestamp = Timestamp.fromDate(new Date(endDate));
      q = query(q, where('createdAt', '<=', endTimestamp));
    }

    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ 
      ...doc.data(),
      eventId: doc.id 
    } as Event));
  }
  
  async getEventSignUps(
    startDate?: string, 
    endDate?: string, 
    hideEndedEvents: boolean = false
  ): Promise<EventSignUp[]> {
    let q = query(
      collection(db, EVENT_SIGNUP_COLLECTION), 
      orderBy('timestamp', 'desc')
    );
    
    // Apply date filters
    if (startDate) {
      const startTimestamp = Timestamp.fromDate(new Date(startDate));
      q = query(q, where('timestamp', '>=', startTimestamp));
    }

    if (endDate) {
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);
      const endTimestamp = Timestamp.fromDate(endDateTime);
      q = query(q, where('timestamp', '<=', endTimestamp));
    }

    const snapshot = await getDocs(q);
    let signUps = snapshot.docs.map(doc => ({
      ...doc.data(),
    } as EventSignUp));

    // Filter ended events (client-side since it requires joining with events)
    if (hideEndedEvents) {
      const events = await eventService.getEventsQuery(startDate, endDate);
      const now = Timestamp.now();

      const activeEventIds = new Set(
        events
          .filter(event => event.endDate.seconds >= now.seconds)
          .map(event => event.eventId)
      );

      signUps = signUps.filter(signUp => activeEventIds.has(signUp.eventId));
    }
    return signUps;
  }

  async getEventById(eventId: string): Promise<Event | null> {
    const q = query(collection(db, EVENT_COLLECTION), where('eventId', '==', eventId));
    const snap = await getDocs(q);
    const doc = snap.docs[0];
    return doc ? { 
      ...doc.data(),
      eventId: doc.id 
    } as Event : null;
  }

  async createEvent(event: Event): Promise<void> {
    await setDoc(doc(db, EVENT_COLLECTION, event.eventId), event);
  }

  async updateEvent(eventId: string, event: Partial<Event>): Promise<void> {
    const { eventId: _, ...eventData } = event;
    await updateDoc(doc(db, EVENT_COLLECTION, eventId), eventData);
  }

  async deleteEvent(eventId: string): Promise<void> {
    await deleteDoc(doc(db, EVENT_COLLECTION, eventId));
  }
}

export const eventService = new EventService(); 